import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Divider,
  InputAdornment,
  CircularProgress,
  Backdrop,
  IconButton
} from '@mui/material';
import { Link } from 'lucide-react';
import ProductTools from './ProductTools';

const AchatForm = ({ 
  open, 
  onClose, 
  onSubmit, 
  editingId, 
  initialData, 
  fournisseurs, 
  produits, 
  statuts,
  isCreatingListing 
}) => {
  const [formData, setFormData] = useState({
    dateAchat: "",
    SKUID: "",
    Fournisseur: "",
    Quantité: "",
    prixAchat: "",
    stockRestant: "",
    Statut: "",
    Produit: "",
    dateLivraison: "",
    frais_preparation: "",
    frais_livraison: "",
    TVADeductible: false,
    TxTVA: "20",
    URL: ""
  });

  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.Produit) {
        const product = produits.find(p => p.id === initialData.Produit);
        setSelectedProduct(product);
      }
    }
  }, [initialData, produits]);

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    setFormData(prev => ({
      ...prev,
      Produit: newValue ? newValue.id : ""
    }));
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  return (
    <Dialog
      open={open}
      onClose={isCreatingListing ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {editingId ? "Éditer un Achat" : "Ajouter un Achat"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Section Produit et Image */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{
              p: 2,
              height: '100%',
              bgcolor: 'linear-gradient(135deg, #e8f9ef 0%, #f8fafc 100%)',
              boxShadow: '0 4px 24px 0 rgba(40, 167, 69, 0.10)',
              borderRadius: 3,
              border: '2px solid #28A745',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#28A745', letterSpacing: 1 }}>
                Produit
              </Typography>
              <Autocomplete
                options={produits}
                getOptionLabel={(option) => `${option.Titre} (${option.EAN})`}
                value={selectedProduct}
                onChange={handleProductChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produit"
                    margin="normal"
                    fullWidth
                    sx={{
                      fontSize: '1.15rem',
                      '& .MuiInputBase-root': {
                        fontSize: '1.15rem',
                        height: 56,
                        borderRadius: 2,
                        background: '#fff',
                        boxShadow: '0 1px 4px 0 rgba(40, 167, 69, 0.07)'
                      },
                      mb: 2,
                      mt: 1
                    }}
                  />
                )}
                sx={{ width: '100%', maxWidth: 320, mx: 'auto', mb: 2 }}
              />
              <TextField
                label="SKUID"
                fullWidth
                margin="normal"
                value={formData.SKUID}
                onChange={e => setFormData({ ...formData, SKUID: e.target.value })}
                sx={{
                  fontSize: '1.05rem',
                  '& .MuiInputBase-root': {
                    fontSize: '1.05rem',
                    height: 48,
                    borderRadius: 2,
                    background: '#fff',
                  },
                  mb: 2
                }}
              />
              {selectedProduct && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Avatar
                    variant="rounded"
                    src={selectedProduct.ASIN ? `https://images-na.ssl-images-amazon.com/images/P/${selectedProduct.ASIN}.01._SCLZZZZZZZ_.jpg` : ''}
                    alt={selectedProduct.Titre}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mx: 'auto', 
                      mb: 1,
                      bgcolor: '#f5f5f5',
                      border: '1px solid #eee'
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    ASIN: {selectedProduct.ASIN || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    EAN: {selectedProduct.EAN || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                    Stock restant: {selectedProduct.stockRestant || '0'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Prix moyen: {selectedProduct["Prix de vente moyen"] ? `${Number(selectedProduct["Prix de vente moyen"]).toFixed(2)} €` : '-'}
                  </Typography>
                  {selectedProduct.ASIN && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <ProductTools asin={selectedProduct.ASIN} />
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Section Informations principales */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {/* Dates */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date d'achat"
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={formatDateForInput(formData.dateAchat)}
                  onChange={(e) =>
                    setFormData({ ...formData, dateAchat: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date de livraison"
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={formatDateForInput(formData.dateLivraison)}
                  onChange={(e) =>
                    setFormData({ ...formData, dateLivraison: e.target.value })
                  }
                />
              </Grid>

              {/* Fournisseur et URL */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={fournisseurs}
                  getOptionLabel={(option) => option.Titre}
                  value={fournisseurs.find((f) => f.id === formData.Fournisseur) || null}
                  onChange={(event, newValue) =>
                    setFormData({
                      ...formData,
                      Fournisseur: newValue ? newValue.id : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Fournisseur"
                      margin="dense"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="URL"
                  fullWidth
                  margin="dense"
                  value={formData.URL}
                  onChange={(e) =>
                    setFormData({ ...formData, URL: e.target.value })
                  }
                  InputProps={{
                    endAdornment: formData.URL && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => window.open(formData.URL, '_blank', 'noopener,noreferrer')}
                        >
                          <Link size={16} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Prix et Quantité */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Prix d'achat"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.prixAchat}
                  onChange={(e) =>
                    setFormData({ ...formData, prixAchat: e.target.value })
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Quantité"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.Quantité}
                  onChange={(e) =>
                    setFormData({ ...formData, Quantité: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Stock restant"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.stockRestant || ""}
                  disabled
                  helperText="Donnée alimentée par Amazon"
                />
              </Grid>

              {/* Frais */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Frais de préparation"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.frais_preparation}
                  onChange={(e) =>
                    setFormData({ ...formData, frais_preparation: e.target.value })
                  }
                  onBlur={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      frais_preparation:
                        val !== '' && !isNaN(val) ? Number(val).toFixed(2) : val,
                    });
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Frais de livraison"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.frais_livraison}
                  onChange={(e) =>
                    setFormData({ ...formData, frais_livraison: e.target.value })
                  }
                  onBlur={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      frais_livraison:
                        val !== '' && !isNaN(val) ? Number(val).toFixed(2) : val,
                    });
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                />
              </Grid>

              {/* TVA */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.TVADeductible}
                        onChange={(e) =>
                          setFormData({ ...formData, TVADeductible: e.target.checked })
                        }
                      />
                    }
                    label="TVA déductible"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Taux de TVA (%)"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={formData.TxTVA}
                  onChange={(e) =>
                    setFormData({ ...formData, TxTVA: e.target.value })
                  }
                />
              </Grid>

              {/* Statut */}
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.Statut}
                    label="Statut"
                    onChange={(e) =>
                      setFormData({ ...formData, Statut: e.target.value })
                    }
                  >
                    {statuts.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.Titre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={isCreatingListing}
        >
          Annuler
        </Button>
        <Button 
          onClick={() => onSubmit(formData)} 
          variant="contained" 
          color="primary"
          disabled={isCreatingListing}
        >
          {editingId ? "Mettre à jour" : "Enregistrer"}
        </Button>
      </DialogActions>

      {/* Loader qui couvre toute la modal */}
      <Backdrop
        sx={{
          position: 'absolute',
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={isCreatingListing}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" color="inherit">
          Création du listing en cours...
        </Typography>
      </Backdrop>
    </Dialog>
  );
};

export default AchatForm; 